import React, { useMemo } from "react";
import { Dialog, DialogContent, DialogHeader } from "./ui/dialog";
import { ImageWithFallback } from "./figma/ImageWithFallback";
import { useQuery } from "@tanstack/react-query";
import { getConfiguration } from "../api/settingsApi";
import Logo from "../assets/data-lab-logo.png";

function SandboxTermsContent() {
  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-900">
        Data Lab Terms and Conditions for Sandbox Environment (University
        Hosted)
      </h3>

      <div className="space-y-3 text-sm text-gray-800 leading-6">
        <section>
          <p className="font-medium">1. Purpose</p>
          <p>
            The Sandbox Environment is provided by Data Lab to enable students
            to perform data science project work as part of their coursework. It
            is intended strictly for educational and research purposes within
            the scope of assigned academic activities.
          </p>
        </section>

        <section>
          <p className="font-medium">2. Provisioning and Usage</p>
          <p>
            Each launch provisions a dedicated JupyterLab environment with
            pre-installed Python data science libraries and access to project
            datasets. The environment is temporary and will automatically shut
            down after 8 hours or upon project submission. Provisioning
            typically takes 2–3 minutes. Work performed in the sandbox is
            automatically saved and versioned at regular intervals. The sandbox
            will be available only for the duration specified for the assignment
            or project, within which the student must complete all required
            work. If additional time is required, the student may submit a
            request for an extension, which must be approved by the course
            instructor before continued access is granted. Repeated provisioning
            may be limited to ensure fair access for all students.
          </p>
        </section>

        <section>
          <p className="font-medium">3. Data and Storage</p>
          <p>
            All code, files, and datasets uploaded by students are their own
            responsibility. Data and notebooks may be periodically accessed by
            faculty or platform administrators for academic review, evaluation,
            or technical troubleshooting. Temporary environments are not
            intended for long-term data storage. Data may be deleted
            automatically after environment shutdown or course completion.
          </p>
        </section>

        <section>
          <p className="font-medium">4. Acceptable Use Policy</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>
              Use the sandbox only for academic, non-commercial purposes related
              to coursework.
            </li>
            <li>
              Do not upload, download, or share copyrighted, confidential, or
              malicious content.
            </li>
            <li>
              Do not run unauthorized software, external services, or unrelated
              long-running tasks.
            </li>
            <li>
              Do not bypass access controls, share credentials, or disrupt
              system operations.
            </li>
          </ul>
          <p className="mt-2">
            Violations may lead to immediate suspension of access and
            disciplinary action as per University policy.
          </p>
        </section>

        <section>
          <p className="font-medium">5. Intellectual Property</p>
          <p>
            Students retain ownership of their original code, models, and
            analysis. The University may retain anonymized copies of work for
            academic assessment, quality review, or research reporting. Shared
            datasets and starter code are provided for educational use only and
            may not be redistributed.
          </p>
        </section>

        <section>
          <p className="font-medium">6. Privacy and Data Protection</p>
          <p>
            Personal information, activity logs, and usage data may be recorded
            for security, monitoring, and academic tracking purposes. Data Lab
            will handle all data in compliance with its data protection and
            privacy policies. No personal data will be shared externally except
            with approved infrastructure service providers for environment
            provisioning.
          </p>
        </section>

        <section>
          <p className="font-medium">7. Liability and Service Availability</p>
          <p>
            The Sandbox Environment is provided on a best-effort basis. Data Lab
            is not liable for data loss, environment interruptions, or issues
            arising from user actions or external dependencies. Students are
            advised to export and back up their important work regularly.
          </p>
        </section>

        <section>
          <p className="font-medium">8. Termination of Access</p>
          <p>
            Access to the Sandbox Environment may be suspended or revoked at any
            time due to misuse or violation of these terms, completion or
            withdrawal from the course, expiry of the specified sandbox duration
            without an approved extension, or scheduled maintenance and
            technical upgrades.
          </p>
        </section>

        <section>
          <p className="font-medium">9. Acceptance</p>
          <p>
            By selecting “I understand the provisioning process and agree to the
            terms and conditions” and launching the sandbox, you confirm that
            you have read, understood, and accepted these Terms and Conditions.
          </p>
        </section>
      </div>
    </div>
  );
}

export default function SandboxTermsModal({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const configurationQuery = useQuery({
    queryKey: ["/settings"],
    queryFn: getConfiguration,
    refetchOnWindowFocus: false,
  });

  return (
    <div className="space-y-4">
      {/* Modal with single close button */}
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl bg-white border">
          <DialogHeader className="flex flex-row items-start justify-between">
            {/* Header outside the modal */}
            <div className="flex flex-row items-center gap-3">
              <div className="w-10 h-10 relative">
                <ImageWithFallback
                  src={configurationQuery?.data?.data?.logoUrl || Logo}
                  alt="Data Lab Logo"
                  className="w-full h-full object-contain"
                />
              </div>
              <div>
                <h2 className="font-semibold text-bits-blue">
                  {configurationQuery?.data?.data?.title || "Data Lab"}
                </h2>
                <p className="text-xs text-muted-foreground">
                  Data Science Projects Platform
                </p>
              </div>
            </div>
          </DialogHeader>

          <div className="max-h-[70vh] overflow-y-auto custom-scroll">
            <SandboxTermsContent />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
